import { KeyboardEvent, useEffect, useRef } from 'react'
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'

export type QuantityKeypadProps = {
	/** Digits entered since the pad opened. Empty means nothing typed yet. */
	typed: string,
	onTypedChange: ( next: string ) => void,
	/** Enter, or the Done key. */
	onDone: () => void,
	doneLabel?: string,
	/** Accessible name for the pad, e.g. the product being counted. */
	label: string,
}

const KEYPAD_DIGITS = [ '1', '2', '3', '4', '5', '6', '7', '8', '9' ]

/**
 * Number entry for a bench or a tablet.
 *
 * The on-screen keys are for a gloved thumb; a hardware keyboard drives the
 * same pad through the key handler, so a packer at a desk types and a packer
 * holding a tablet taps without either of them choosing a mode first.
 */
export default function QuantityKeypad( {
	typed,
	onTypedChange,
	onDone,
	doneLabel = 'Done',
	label,
}: QuantityKeypadProps ) {
	const theme = useTheme()
	const padRef = useRef<HTMLDivElement>( null )

	/*
	  Take focus once the pad is on screen.

	  `autoFocus` did not do this. Browsers honour that attribute on form
	  controls, not on a div, and both callers mount this pad inside a MUI
	  Popover, which focuses its own Paper when it opens. So focus sat on the
	  Paper — an ancestor of this pad — and because key events travel upward, the
	  handler below never saw a keystroke. Typing was silently dead.

	  Focusing here works with the Popover rather than against it: this effect
	  runs before the Popover's own, and the Popover only claims focus when it is
	  not already somewhere inside, so it leaves this alone.
	*/
	useEffect( () => {
		padRef.current?.focus()
	}, [] )

	const handleKeyDown = ( event: KeyboardEvent<HTMLElement> ) => {
		if ( /^[0-9]$/.test( event.key ) ) {
			event.preventDefault()
			onTypedChange( `${typed}${event.key}` )
			return
		}
		if ( event.key === 'Backspace' ) {
			event.preventDefault()
			onTypedChange( typed.slice( 0, -1 ) )
			return
		}
		if ( event.key === 'Enter' ) {
			event.preventDefault()
			onDone()
		}
	}

	return (
		<Box
			// Focused so hardware keys land here, but not a tab stop of its own —
			// the buttons inside already are.
			ref={padRef}
			tabIndex={-1}
			onKeyDown={handleKeyDown}
			aria-label={`Quantity keypad for ${label}`}
			sx={{
				p: 1.5,
				bgcolor: 'pickFlow.keypadBackground',
				display: 'grid',
				gridTemplateColumns: 'repeat(3, 1fr)',
				gap: `${theme.touch.gap}px`,
				outline: 'none',
			}}
		>
			{KEYPAD_DIGITS.map( ( digit ) => (
				<Button
					key={digit}
					variant="contained"
					color="inherit"
					onClick={() => onTypedChange( `${typed}${digit}` )}
					sx={{ minWidth: theme.touch.keypadKey, minHeight: theme.touch.keypadKey, bgcolor: 'pickFlow.surface' }}
				>
					<Typography variant="h5" component="span">{digit}</Typography>
				</Button>
			) )}
			<Button
				aria-label={`Clear quantity for ${label}`}
				variant="contained"
				color="inherit"
				onClick={() => onTypedChange( '' )}
				sx={{ minWidth: theme.touch.keypadKey, minHeight: theme.touch.keypadKey, bgcolor: 'pickFlow.surface' }}
			>
				<BackspaceOutlinedIcon />
			</Button>
			<Button
				variant="contained"
				color="inherit"
				onClick={() => onTypedChange( `${typed}0` )}
				sx={{ minWidth: theme.touch.keypadKey, minHeight: theme.touch.keypadKey, bgcolor: 'pickFlow.surface' }}
			>
				<Typography variant="h5" component="span">0</Typography>
			</Button>
			<Button
				variant="contained"
				onClick={onDone}
				sx={{ minWidth: theme.touch.keypadKey, minHeight: theme.touch.keypadKey }}
			>
				{doneLabel}
			</Button>
		</Box>
	)
}
